import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ballColor } from '@photo-merge/shared';
import { Button } from '../components/Button';
import {
  DEFAULT_CROP,
  MAX_ZOOM,
  MIN_ZOOM,
  type CropTransform,
  clampCrop,
  panByPixels,
  placeImage,
  zoomAt,
} from './crop';
import type { Photo } from './photos';
import styles from './CropEditor.module.css';

interface CropEditorProps {
  photo: Photo;
  rank: number;
  count: number;
  onCommit: (crop: CropTransform) => void;
  onCancel: () => void;
}

/** Fraction of the shorter stage dimension the crop circle occupies. */
const CIRCLE_FILL = 0.82;

/** Multiplicative, so each tap changes the view by the same proportion at any zoom. */
const ZOOM_STEP = 1.35;

export function CropEditor({ photo, rank, count, onCommit, onCancel }: CropEditorProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [crop, setCrop] = useState<CropTransform>(photo.crop);
  const [box, setBox] = useState({ width: 0, height: 0, circle: 0 });

  // Live pointer state. Kept in a ref because it changes far faster than React should
  // re-render, and a stale closure here would make dragging jump.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ distance: number; midX: number; midY: number } | null>(null);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () => {
      const r = stage.getBoundingClientRect();
      setBox({ width: r.width, height: r.height, circle: Math.min(r.width, r.height) * CIRCLE_FILL });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  // Escape to back out, matching the header's cancel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  /**
   * Always updates from the previous transform rather than the one captured in this
   * render's closure. Pointer events fire faster than React re-renders, so reading
   * `crop` directly makes a fast drag lag behind the finger.
   */
  const update = useCallback(
    (fn: (previous: CropTransform) => CropTransform) =>
      setCrop((previous) => clampCrop(photo.width, photo.height, fn(previous))),
    [photo.width, photo.height],
  );

  // Encoding the working copy is expensive and the source never changes, so it must
  // not happen on every pointer move.
  const src = useMemo(() => photo.image.toDataURL(), [photo.image]);

  // --- Gestures ---

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) pinch.current = readPinch();
  };

  const readPinch = () => {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return null;
    return {
      distance: Math.hypot(a.x - b.x, a.y - b.y),
      midX: (a.x + b.x) / 2,
      midY: (a.y + b.y) / 2,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const previous = pointers.current.get(e.pointerId);
    if (!previous) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2) {
      const now = readPinch();
      const before = pinch.current;
      if (!now || !before || before.distance === 0) return;
      pinch.current = now;

      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) return;
      const focusX = now.midX - rect.left - box.width / 2;
      const focusY = now.midY - rect.top - box.height / 2;
      const ratio = now.distance / before.distance;
      const dx = now.midX - before.midX;
      const dy = now.midY - before.midY;

      // Zoom about the pinch centre, then pan by however far that centre travelled.
      update((prev) =>
        panByPixels(zoomAt(prev, ratio, focusX, focusY, box.circle), dx, dy, box.circle),
      );
      return;
    }

    const dx = e.clientX - previous.x;
    const dy = e.clientY - previous.y;
    update((prev) => panByPixels(prev, dx, dy, box.circle));
  };

  const endPointer = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    // Re-baseline so lifting one finger of a pinch doesn't jump the image.
    pinch.current = pointers.current.size === 2 ? readPinch() : null;
  };

  const onWheel = (e: React.WheelEvent) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const focusX = e.clientX - rect.left - box.width / 2;
    const focusY = e.clientY - rect.top - box.height / 2;
    const factor = Math.exp(-e.deltaY * 0.0016);
    update((prev) => zoomAt(prev, factor, focusX, focusY, box.circle));
  };

  // --- Layout ---

  const placement = placeImage(photo.width, photo.height, box.circle, crop);
  const circleLeft = (box.width - box.circle) / 2;
  const circleTop = (box.height - box.circle) / 2;
  const ring = Math.max(3, box.circle * 0.035);

  return (
    <div className={styles.backdrop}>
      <header className={styles.head}>
        <div>
          <div className={styles.title}>Ball {rank}</div>
          <div className={styles.subtitle}>Drag to move · pinch or scroll to zoom</div>
        </div>
        <button className={styles.iconButton} onClick={onCancel} aria-label="Cancel">
          ✕
        </button>
      </header>

      <div
        ref={stageRef}
        className={styles.stage}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onWheel={onWheel}
      >
        {box.circle > 0 ? (
          <>
            <img
              className={styles.photo}
              src={src}
              alt=""
              style={{
                left: circleLeft + placement.x,
                top: circleTop + placement.y,
                width: placement.width,
                height: placement.height,
              }}
              draggable={false}
            />
            <div
              className={styles.mask}
              style={{
                left: circleLeft,
                top: circleTop,
                width: box.circle,
                height: box.circle,
              }}
            />
            <div
              className={styles.ring}
              style={{
                left: circleLeft,
                top: circleTop,
                width: box.circle,
                height: box.circle,
                border: `${ring}px solid ${ballColor(rank, count)}`,
              }}
            />
            <p className={styles.hint}>Fit the face inside the circle</p>
          </>
        ) : null}
      </div>

      <footer className={styles.foot}>
        <button
          className={styles.iconButton}
          onClick={() => update(() => DEFAULT_CROP)}
          aria-label="Reset crop"
          title="Reset"
        >
          ↺
        </button>

        <div className={styles.zoomRow}>
          <button
            className={styles.iconButton}
            disabled={crop.zoom <= MIN_ZOOM}
            onClick={() => update((prev) => ({ ...prev, zoom: prev.zoom / ZOOM_STEP }))}
            aria-label="Zoom out"
          >
            −
          </button>
          <span className={styles.zoomValue} aria-hidden="true">
            {crop.zoom.toFixed(1)}×
          </span>
          <button
            className={styles.iconButton}
            disabled={crop.zoom >= MAX_ZOOM}
            onClick={() => update((prev) => ({ ...prev, zoom: prev.zoom * ZOOM_STEP }))}
            aria-label="Zoom in"
          >
            +
          </button>
        </div>

        <Button onClick={() => onCommit(crop)}>Done</Button>
      </footer>
    </div>
  );
}
